import { useRef, ReactNode } from 'react';
import { useReduxState } from 'hooks';
import { ModalButtonWithForm, User } from 'components/common';
import { FormGroup, EmailMessageEditor } from 'components/common/form';

import type { FormApi, FormValues } from 'informed';
import type { EmailTemplateData, UserWithBasicData } from 'shared-types';
import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  buttonCategory?: ButtonCategory;
  buttonDisabled?: boolean;
  buttonIcon?: IconProp;
  buttonIsDropdown?: boolean;
  buttonSize?: ButtonSize;
  buttonText?: string;
  buttonTooltip?: string;
  children?: ReactNode;
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  id: string;
  modalTitle?: string;
  onSubmit: (
    data: {
      email: EmailTemplateData['email'];
      targetUserEmailAddress: string;
    } & { [key: string]: any }
  ) => void;
  targetUser: UserWithBasicData;
}

const SendEmail = ({
  buttonCategory,
  buttonDisabled,
  buttonIcon,
  buttonIsDropdown,
  buttonSize,
  buttonText,
  buttonTooltip,
  children,
  emailTemplates,
  emailTemplatesDefault,
  id,
  modalTitle,
  onSubmit,
  targetUser
}: Props) => {
  const formApi = useRef<FormApi<FormValues>>(undefined);

  const { messages } = useReduxState();

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    }
  };

  const handleSubmit = async (values) => {
    await onSubmit({
      ...values,
      email: {
        buttonEnabled: values.email.buttonEnabled,
        buttonLink: values.email.buttonLink,
        buttonText: values.email.buttonText,
        content: values.email.content,
        contentAfterButton: values.email.contentAfterButton,
        footnote: values.email.footnote,
        heading: values.email.heading,
        subject: values.email.subject
      },
      targetUserEmailAddress: targetUser.email
    });

    formApi.current.reset();
  };

  return (
    <ModalButtonWithForm
      apiRef={formApi}
      buttonCategory={buttonCategory}
      buttonDisabled={buttonDisabled}
      buttonIcon={buttonIcon || 'paper-plane'}
      buttonIsDropdown={buttonIsDropdown}
      buttonSize={buttonSize}
      buttonText={buttonText}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSend}
      modalTitle={modalTitle || messages.usersSendMessage}
      modalWidth="wide"
      onSubmit={handleSubmit}
      {...(buttonTooltip
        ? {
            buttonProps: {
              'aria-label': buttonTooltip,
              'data-tooltip': buttonTooltip
            }
          }
        : {})}
    >
      {() => (
        <>
          {children}

          <FormGroup label={messages.commonRecipient}>
            <User enableProfileLink={true} user={targetUser} />
          </FormGroup>

          <EmailMessageEditor
            availableTemplates={emailTemplates}
            id={`send-email-${id}`}
            style={{ marginTop: 20 }}
          />
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default SendEmail;
